import { Controller } from "@hotwired/stimulus"

// Click-to-play video. data-src is OUR media-domain URL of record
// (ig_video/<id>, populated by the bg sync workers); data-fbcdn carries
// the raw Meta CDN url as the one-shot fallback while the S3 object
// doesn't exist yet.
export default class extends Controller {
  static targets = ["thumbnail", "player", "overlay"]

  // On a cold miss our media domain 404s briefly while the bg worker heals
  // the object. Instead of giving up to the (expired) raw Meta CDN url, poll
  // our own URL with a ?retry=<ts> cache-buster a few times — the worker's
  // ?retry= guard lets the request through without spawning duplicate sync
  // work. Only fall back to data-fbcdn once the retries are exhausted, so
  // already-healed videos still play on the first click.
  static MAX_RETRIES = 3
  static RETRY_DELAY = 2500

  connect() {
    console.log("[video-preview] connect", {
      src: this.hasPlayerTarget ? this.playerTarget.dataset.src : null,
      has_fbcdn_fallback: this.hasPlayerTarget ? !!this.playerTarget.dataset.fbcdn : false
    })
  }

  play() {
    if (this.hasThumbnailTarget) this.thumbnailTarget.classList.add("hidden")
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("hidden")
    const video = this.playerTarget
    this._retries = 0
    console.log("[video-preview] play", { src: video.dataset.src })

    if (!this._onError) {
      this._onError = () => {
        const base = video.dataset.src
        const fbcdn = video.dataset.fbcdn

        if (base && this._retries < this.constructor.MAX_RETRIES) {
          this._retries += 1
          const retryUrl = `${base}${base.includes("?") ? "&" : "?"}retry=${Date.now()}`
          console.log("[video-preview] source failed — retrying media domain", {
            attempt: this._retries, max: this.constructor.MAX_RETRIES, retryUrl: retryUrl.slice(0, 120)
          })
          this._retryTimer = setTimeout(() => {
            video.src = retryUrl
            video.play()
          }, this.constructor.RETRY_DELAY)
          return
        }

        if (fbcdn && video.src !== fbcdn) {
          console.log("[video-preview] media retries exhausted — falling back to data-fbcdn", { fbcdn: fbcdn.slice(0, 100) })
          video.src = fbcdn
          video.play()
        } else {
          console.log("[video-preview] playback failed — no working source left")
        }
      }
      video.addEventListener("error", this._onError)
    }

    video.src = video.dataset.src
    video.classList.remove("hidden")
    video.play()
  }

  disconnect() {
    if (this._retryTimer) clearTimeout(this._retryTimer)
    if (this._onError && this.hasPlayerTarget) {
      this.playerTarget.removeEventListener("error", this._onError)
    }
  }
}
